/**
 * DSM (Device State Monitor) — fleet dashboard.
 *
 * Live-wired against the dsm-service (`/dsm/device-status`). See
 * `services/dsmService.js`. Filters in the left rail map directly onto
 * query parameters; KPIs are derived from the loaded page (and `total`
 * for the "Total Devices" card). Pagination is API-backed.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  listDeviceStatuses,
  getDsmHealth,
  getDsmReadiness,
} from '../../services/dsmService';
import organizationService from '../../services/organizationService';
import './dsm.css';

// Filter state — `null` means "no filter". Status & in-rack are tri-state.
// `orgId` drives the cascade for the Site dropdown; `org` (name) is what
// actually goes to the DSM API. Same for `site`.
const INITIAL_FILTERS = {
  orgId: '',
  org: '',
  site: '',
  online: null,
  inRack: null,
  pairedUser: '',
  search: '', // client-side only — API has no `search` param.
};

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------
function formatOrgSite(d) {
  if (!d.org && !d.site) return '—';
  if (d.org && d.site) return `${d.org} / ${d.site}`;
  return d.org || d.site;
}

function formatHeartbeat(iso) {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '—';
  const sec = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin ? `${hr}h ${String(remMin).padStart(2, '0')}m` : `${hr}h`;
}

function formatAbs(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formatCapacity(c) {
  if (c == null || c === '') return null;
  const s = String(c).trim();
  return s.endsWith('%') ? s : `${s}%`;
}

function capacityNumber(c) {
  if (c == null || c === '') return null;
  const n = parseFloat(String(c).replace('%', ''));
  return Number.isFinite(n) ? n : null;
}

function StatusPill({ online }) {
  return online ? <span className="pill green">Online</span> : <span className="pill gray">Offline</span>;
}

function InRackPill({ value }) {
  if (value === true)  return <span className="pill green-ghost">Yes</span>;
  if (value === false) return <span className="pill red-ghost">No</span>;
  return <span className="muted">—</span>;
}

// Location-services feature test: `brightness` comes straight from the DSM API
// when the device reports it. Until DSM/location-services supplies a value, the
// field is absent on the response, so we render an explicit "NULL" (rather than
// the "—" used elsewhere) so QA can tell "field present but unreported" apart
// from a plain empty cell. When the API starts returning a value it is displayed
// automatically — no further UI change needed.
function NullableValue({ value, suffix = '' }) {
  const empty = value === null || value === undefined || value === '';
  if (empty) return <span className="muted">NULL</span>;
  return <>{value}{suffix}</>;
}

function rowClassFor(d) {
  if (!d.online) return 'row-offline';
  const cap = capacityNumber(d.capacity);
  const lowBatt = cap !== null && cap < 25;
  const hotBatt = d.batteryTemp != null && d.batteryTemp > 45;
  return lowBatt || hotBatt ? 'row-warn' : '';
}

function DeviceRow({ d, onClick }) {
  const cap     = capacityNumber(d.capacity);
  const lowBatt = cap !== null && cap < 25;
  const hotBatt = d.batteryTemp != null && d.batteryTemp > 45;

  // NOTE: We intentionally render the same columns for online AND offline
  // devices — the API still returns the last-known capacity / batteryTemp /
  // sysTemp / chargeState / inRack for offline rows, and hiding them makes
  // the table look mostly empty. Offline state is already communicated by
  // the Status pill, the muted `row-offline` row tint, and the red-coloured
  // heartbeat.
  return (
    <tr className={rowClassFor(d)} onClick={onClick} style={{ cursor: 'pointer' }}>
      <td className="mono">{d.mac}</td>
      <td>{formatOrgSite(d)}</td>
      <td><StatusPill online={!!d.online} /></td>
      <td className={lowBatt ? 'text-amber' : ''}>
        {formatCapacity(d.capacity) ?? '—'}{lowBatt ? ' ⚠' : ''}
      </td>
      <td>
        {String(d.chargeState || '').toLowerCase() === 'charging'
          ? <span className="text-blue">{d.chargeState}</span>
          : (d.chargeState ?? '—')}
      </td>
      <td><InRackPill value={d.inRack} /></td>
      <td className={hotBatt ? 'text-red' : ''}>
        {d.batteryTemp != null ? `${Number(d.batteryTemp).toFixed(1)}${hotBatt ? ' ⚠' : ''}` : '—'}
      </td>
      <td>{d.sysTemp ?? '—'}</td>
      <td><NullableValue value={d.brightness} /></td>
      <td className={d.pairedUser ? '' : 'muted'}>{d.pairedUser ?? '—'}</td>
      <td>{d.zoneName ?? '—'}</td>
      <td>{d.appVersion ?? '—'}</td>
      <td className={d.online ? '' : 'text-red'}>{formatHeartbeat(d.heartbeatUpdatedAt)}</td>
    </tr>
  );
}

function DetailDrawer({ device, onClose }) {
  const d = device || {};
  return (
    <aside className="drawer">
      <div className="drawer-header">
        <div>
          <div className="drawer-title">Device Detail</div>
          <div className="mono small">{d.mac ?? '—'}</div>
        </div>
        <button className="btn-ghost" onClick={onClose}>✕</button>
      </div>
      <div className="drawer-body">
        <div className="detail-section">
          <h4>Identification</h4>
          <div className="kv"><span>MAC</span><b className="mono">{d.mac ?? '—'}</b></div>
          <div className="kv"><span>Organization</span><b>{d.org ?? '—'}</b></div>
          <div className="kv"><span>Site</span><b>{d.site ?? '—'}</b></div>
          <div className="kv"><span>Client Type</span><b>{d.clientType ?? '—'}</b></div>
          <div className="kv"><span>App Version</span><b>{d.appVersion ?? '—'}</b></div>
        </div>
        <div className="detail-section">
          <h4>State</h4>
          <div className="kv"><span>Online</span><b className={d.online ? 'text-green' : 'text-red'}>{d.online ? 'Yes' : 'No'}</b></div>
          <div className="kv"><span>In Rack</span><b className={d.inRack ? 'text-green' : 'text-red'}>{d.inRack ? 'Yes' : 'No'}</b></div>
          <div className="kv"><span>Charge State</span><b>{d.chargeState ?? '—'}</b></div>
          <div className="kv"><span>Capacity</span><b>{formatCapacity(d.capacity) ?? '—'}</b></div>
          <div className="kv">
            <span>Battery Temp</span>
            <b className={d.batteryTemp != null && d.batteryTemp > 45 ? 'text-red' : ''}>
              {d.batteryTemp != null ? `${Number(d.batteryTemp).toFixed(1)}°C${d.batteryTemp > 45 ? ' ⚠' : ''}` : '—'}
            </b>
          </div>
          <div className="kv"><span>System Temp</span><b>{d.sysTemp != null ? `${d.sysTemp}°C` : '—'}</b></div>
          <div className="kv"><span>Volume</span><b>{d.volume != null ? `${d.volume}%` : '—'}</b></div>
          <div className="kv"><span>Brightness</span><b><NullableValue value={d.brightness} /></b></div>
        </div>
        <div className="detail-section">
          <h4>Pairing &amp; Location</h4>
          <div className="kv"><span>Paired User</span><b>{d.pairedUser ?? '—'}</b></div>
          <div className="kv"><span>Zone</span><b>{d.zoneName ?? '—'}</b></div>
          <div className="kv"><span>Pairing Updated</span><b>{formatAbs(d.pairingUpdatedAt)}</b></div>
          <div className="kv"><span>Location Updated</span><b>{formatAbs(d.locationUpdatedAt)}</b></div>
        </div>
        <div className="detail-section">
          <h4>Timestamps</h4>
          <div className="kv"><span>Heartbeat</span><b>{formatHeartbeat(d.heartbeatUpdatedAt)} ago</b></div>
          <div className="kv"><span>Created</span><b>{formatAbs(d.createdAt)}</b></div>
          <div className="kv"><span>Updated</span><b>{formatAbs(d.updatedAt)}</b></div>
        </div>
      </div>
    </aside>
  );
}

export default function DSMPage() {
  const [pending, setPending] = useState(INITIAL_FILTERS);
  const [filters, setFilters] = useState(INITIAL_FILTERS);

  const [page, setPage]         = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [result, setResult] = useState({ items: [], total: 0, page: 1, pageSize: 50, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const [health, setHealth]     = useState({ service: null, db: null });
  const [lastSync, setLastSync] = useState(null);
  const [selected, setSelected] = useState(null);

  // Org / site lookup state for the cascading filter dropdowns.
  const [orgs, setOrgs]               = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [sites, setSites]             = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);

  // Load orgs once on mount.
  useEffect(() => {
    let cancelled = false;
    setOrgsLoading(true);
    organizationService.listOrganizations(0, 200)
      .then((page) => { if (!cancelled) setOrgs(page?.content ?? []); })
      .catch(() => { if (!cancelled) setOrgs([]); })
      .finally(() => { if (!cancelled) setOrgsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Cascade: load sites when the *pending* org changes.
  useEffect(() => {
    let cancelled = false;
    if (!pending.orgId) { setSites([]); return undefined; }
    setSitesLoading(true);
    organizationService.listSites(pending.orgId, 0, 200)
      .then((page) => { if (!cancelled) setSites(page?.content ?? []); })
      .catch(() => { if (!cancelled) setSites([]); })
      .finally(() => { if (!cancelled) setSitesLoading(false); });
    return () => { cancelled = true; };
  }, [pending.orgId]);

  // `silent=true` is used by the polling loop so we don't flash the
  // "Loading devices…" placeholder over the existing data every 5 s.
  const fetchData = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const data = await listDeviceStatuses({
        org:        filters.org || undefined,
        site:       filters.site || undefined,
        online:     filters.online,
        inRack:     filters.inRack,
        pairedUser: filters.pairedUser || undefined,
        page,
        pageSize,
      });
      setResult(data);
      setError(null);
      setLastSync(new Date());
    } catch (e) {
      setError(e.message || 'Failed to load devices');
      if (!silent) setResult({ items: [], total: 0, page: 1, pageSize, pages: 1 });
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters, page, pageSize]);

  // Initial + filter/page-change load (shows spinner).
  useEffect(() => { fetchData(); }, [fetchData]);

  // Live refresh every 5 s. Pauses while the tab is hidden so we don't
  // hammer the API in background tabs, and resumes on visibility change.
  useEffect(() => {
    const POLL_MS = 5000;
    let timer = null;
    const tick = () => {
      if (document.visibilityState === 'visible') {
        fetchData({ silent: true });
      }
    };
    timer = setInterval(tick, POLL_MS);
    const onVis = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [fetchData]);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([getDsmHealth(), getDsmReadiness()]).then(([h, r]) => {
      if (cancelled) return;
      const serviceOk = h.status === 'fulfilled' &&
        ['healthy', 'ok', 'up'].includes(String(h.value?.status || '').toLowerCase());
      const dbOk = (r.status === 'fulfilled' && (r.value?.database === true || r.value?.ready === true))
        || (h.status === 'fulfilled' && ['connected', 'ok', 'up', 'healthy'].includes(String(h.value?.database || '').toLowerCase()));
      setHealth({ service: serviceOk, db: dbOk });
    });
    return () => { cancelled = true; };
  }, []);

  // Client-side filter: only show online devices. The DSM API does not yet
  // support an `online=true` server-side filter, so we drop offline rows
  // here. KPIs are also computed off this filtered set so the page-level
  // counts stay consistent with what's actually rendered.
  const onlineItems = useMemo(
    () => (result.items || []).filter((d) => d.online),
    [result.items],
  );

  const visibleItems = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    if (!q) return onlineItems;
    return onlineItems.filter((d) =>
      [d.mac, d.org, d.site, d.zoneName, d.pairedUser, d.appVersion]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [onlineItems, filters.search]);

  const kpis = useMemo(() => {
    const items = onlineItems;
    const online   = items.filter((d) => d.online).length;
    const offline  = items.filter((d) => !d.online).length;
    const inRack   = items.filter((d) => d.inRack).length;
    const paired   = items.filter((d) => d.pairedUser).length;
    const lowBatt  = items.filter((d) => {
      const c = capacityNumber(d.capacity);
      return c !== null && c < 25;
    }).length;
    const hotBatt  = items.filter((d) => d.batteryTemp != null && d.batteryTemp > 45).length;
    const alerts   = items.filter((d) => {
      const c = capacityNumber(d.capacity);
      const lb = c !== null && c < 25;
      const hb = d.batteryTemp != null && d.batteryTemp > 45;
      return lb || hb;
    }).length;

    return [
      { label: 'Total Devices',           value: items.length,                 valueClass: '',           extraClass: '' },
      { label: 'Online',           value: online,                       valueClass: 'text-green', extraClass: '' },
      { label: 'Offline',          value: offline,                      valueClass: 'text-red',   extraClass: '' },
      { label: 'In Rack/Charging',          value: inRack,                       valueClass: 'text-blue',  extraClass: '' },
      { label: 'Paired with User',           value: paired,                       valueClass: '',           extraClass: '' },
      { label: '🔋 Low Battery (< 25%)',  value: lowBatt,                      valueClass: 'text-amber', extraClass: 'alert-card' },
      { label: '🌡 Overheating (> 45°C)', value: hotBatt,                      valueClass: 'text-red',   extraClass: 'danger-card' },
      { label: '⚠ Alerts',                value: alerts,                       valueClass: 'text-amber', extraClass: 'alert-card' },
    ];
  }, [result]);

  const setPF = (k, v) => setPending((p) => ({ ...p, [k]: v }));

  const applyFilters = () => { setFilters(pending); setPage(1); };
  const resetFilters = () => { setPending(INITIAL_FILTERS); setFilters(INITIAL_FILTERS); setPage(1); };

  const totalPages = Math.max(1, result.pages || 1);
  const showFrom = result.total === 0 ? 0 : (result.page - 1) * result.pageSize + 1;
  const showTo   = Math.min(result.page * result.pageSize, result.total ?? showFrom);

  const pageButtons = useMemo(() => {
    const out = [];
    const tp = totalPages;
    const cur = result.page;
    const window = [cur - 1, cur, cur + 1].filter((n) => n >= 1 && n <= tp);
    const seen = new Set();
    const push = (n) => { if (!seen.has(n) && n >= 1 && n <= tp) { out.push(n); seen.add(n); } };
    push(1);
    if (window[0] > 2) out.push('…');
    window.forEach(push);
    if (window[window.length - 1] < tp - 1) out.push('…');
    push(tp);
    return out;
  }, [totalPages, result.page]);

  return (
    <div className="dsm-root">
      <header className="topbar">
        <div className="brand">
          <div className="logo">DSM</div>
          <div className="brand-text">
            <h1>Device State Monitor</h1>
            <span className="subtitle">Store Communicator Fleet Dashboard</span>
          </div>
        </div>
        <div className="topbar-actions">
          <div className="health-pill">
            <span className={`dot ${health.service ? 'dot-green' : ''}`} />
            {health.service ? 'Service Healthy' : 'Service Unknown'}
          </div>
          {/* <div className="health-pill">
            <span className={`dot ${health.db ? 'dot-green' : ''}`} />
            {health.db ? 'DB Connected' : 'DB Unknown'}
          </div> */}
        </div>
      </header>

      <div className="layout" style={{ gridTemplateColumns: '1fr' }}>
        {/* Filter sidebar hidden for now — will be re-enabled in a future
            iteration. Keep the markup intact so it's easy to restore.
        <aside className="sidebar">
          <div className="sidebar-filters">
            <div className="filter-group">
              <label>Organization</label>
              <select
                value={pending.orgId}
                disabled={orgsLoading}
                onChange={(e) => {
                  const orgId = e.target.value;
                  const o = orgs.find((x) => String(x.orgId) === String(orgId));
                  // Clear the dependent site when org changes.
                  setPending((p) => ({
                    ...p,
                    orgId,
                    org: o ? (o.displayName || o.name || '') : '',
                    site: '',
                  }));
                }}
              >
                <option value="">{orgsLoading ? 'Loading…' : 'All orgs'}</option>
                {orgs.map((o) => (
                  <option key={o.orgId} value={o.orgId}>
                    {o.displayName || o.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Site</label>
              <select
                value={pending.site}
                disabled={!pending.orgId || sitesLoading}
                onChange={(e) => setPF('site', e.target.value)}
              >
                <option value="">
                  {!pending.orgId
                    ? 'Pick an org first'
                    : (sitesLoading ? 'Loading…' : 'All sites')}
                </option>
                {sites.map((s) => (
                  <option key={s.siteId} value={s.siteName}>
                    {s.siteName}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Status</label>
              <select
                value={pending.online === null ? '' : String(pending.online)}
                onChange={(e) => setPF('online', e.target.value === '' ? null : e.target.value === 'true')}
              >
                <option value="">All</option>
                <option value="true">Online</option>
                <option value="false">Offline</option>
              </select>
            </div>
            <div className="filter-group">
              <label>In Rack</label>
              <select
                value={pending.inRack === null ? '' : String(pending.inRack)}
                onChange={(e) => setPF('inRack', e.target.value === '' ? null : e.target.value === 'true')}
              >
                <option value="">Any</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Paired User</label>
              <input type="text" value={pending.pairedUser}
                     onChange={(e) => setPF('pairedUser', e.target.value)}
                     placeholder="e.g. emp.smith" />
            </div>
            <div className="filter-group">
              <label>Search (this page)</label>
              <input type="text" value={pending.search}
                     onChange={(e) => {
                       setPF('search', e.target.value);
                       setFilters((f) => ({ ...f, search: e.target.value }));
                     }}
                     placeholder="MAC, zone, user…" />
            </div>
            <div className="filter-actions">
              <button className="btn-primary" onClick={applyFilters} disabled={loading}>
                {loading ? 'Loading…' : 'Apply'}
              </button>
              <button className="btn-ghost" onClick={resetFilters} disabled={loading}>Reset</button>
            </div>
          </div>
          <div className="sidebar-footer">
            <small>API v1.0.0</small><br />
            <small>Last sync: {lastSync ? lastSync.toLocaleTimeString() : '—'}</small>
          </div>
        </aside>
        */}

        <main className="content">
          <section className="kpi-grid">
            {kpis.map((k) => (
              <div key={k.label} className={`kpi-card ${k.extraClass}`}>
                <div className="kpi-label">{k.label}</div>
                <div className={`kpi-value ${k.valueClass}`}>{k.value}</div>
              </div>
            ))}
          </section>

          <section className="table-wrap">
            <div className="table-scroll">
              <table className="device-table">
                <thead>
                  <tr>
                    <th>MAC</th>
                    <th>Org / Site</th>
                    <th>Status</th>
                    <th>Battery</th>
                    <th>Charge</th>
                    <th>In Rack</th>
                    <th>Batt °C</th>
                    <th>Sys °C</th>
                    <th>Brightness</th>
                    <th>Paired User</th>
                    <th>Zone</th>
                    <th>App</th>
                    <th>Heartbeat</th>
                  </tr>
                </thead>
                <tbody>
                  {error && (
                    <tr><td colSpan={13} className="text-red" style={{ padding: '20px', textAlign: 'center' }}>
                      {error}
                    </td></tr>
                  )}
                  {!error && loading && result.items.length === 0 && (
                    <tr><td colSpan={13} className="muted" style={{ padding: '20px', textAlign: 'center' }}>
                      Loading devices…
                    </td></tr>
                  )}
                  {!error && !loading && visibleItems.length === 0 && (
                    <tr><td colSpan={13} className="muted" style={{ padding: '20px', textAlign: 'center' }}>
                      No devices match the current filters.
                    </td></tr>
                  )}
                  {visibleItems.map((d) => (
                    <DeviceRow key={d.mac} d={d} onClick={() => setSelected(d)} />
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <span className="muted pagination-info">
                Showing {showFrom}–{showTo} of {result.total ?? 0}
              </span>
              <button className="btn-ghost"
                      disabled={loading || result.page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}>
                ‹ Prev
              </button>
              {pageButtons.map((b, i) =>
                b === '…' ? (
                  <span key={`e${i}`}>…</span>
                ) : (
                  <button key={b}
                          className={`page ${b === result.page ? 'active' : ''}`}
                          onClick={() => setPage(b)}
                          disabled={loading}>
                    {b}
                  </button>
                )
              )}
              <button className="btn-ghost"
                      disabled={loading || result.page >= totalPages}
                      onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>
                Next ›
              </button>
              <select className="page-size" value={pageSize}
                      onChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(1); }}>
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n} / page</option>
                ))}
              </select>
            </div>
          </section>
        </main>
      </div>

      <DetailDrawer device={selected} onClose={() => setSelected(null)} />
    </div>
  );
}

